/** CLI status as exposed by the legacy backend. */
export const CLI_STATUS = {
  UNARCHIVED: 1,
  ARCHIVED: 2,
} as const;

export type CliStatus = (typeof CLI_STATUS)[keyof typeof CLI_STATUS];

/** Two flavors the legacy fetchCli helper supports. */
export type CliType = 'cli' | 'dedicated_cli';

/** Row returned by GET /api/obd/cli/{userId} and /api/obd/cli/dedicated/{userId}. */
export interface CliRow {
  cliId: number;
  cli: string;
  cliStatus: CliStatus;
  dialerName?: string;
  pilotNumber?: string | number;
  userName?: string;
  resellerUsername?: string;
  allocationDate?: string;
}

/** Row returned by GET /api/obd/dialers/cli/list — used by Allocate page. */
export interface DialerCliRow {
  dialerId: number;
  dialerName: string;
  cli?: string;
  pilotNumber?: string | number;
  allocatedCliId?: number;
}

/** Row from GET /api/obd/cli/available/{dialerId} — picker for Change CLI. */
export interface AvailableCliRow {
  cliId: number;
  cli: string;
}

/** Row from GET /api/obd/dialers/name/list — flat dialer dropdown source. */
export interface DialerNameRow {
  dialerId: number;
  dialerName: string;
}

/** Row from GET /api/obd/pilot/list. */
export interface PilotNumberRow {
  pilotNumber: string | number;
}

/** Row from GET /api/obd/cli/archive and /api/obd/user/cli/archive/list/{userId}. */
export interface ArchivedCliRow {
  cliId: number;
  cli: string;
  pilotNumber?: string | number;
}

export interface ChangeCliRequest {
  cliId: string;
  allocatedCliId: string;
  dialerId: string;
}

export interface DeallocateCliRequest {
  cliId: number;
  userId: number;
  /** 1 = SUB/SUPER_ADMIN deallocation, 2 = ADMIN. */
  allocated: 1 | 2;
}

export interface UnarchiveCliRequest {
  cliId: number;
  /** 1 = sub/super admin restore, 2 = end user restore. */
  allocated: 1 | 2;
}

export interface UploadCliFilePayload {
  cliFile: File;
  userId: number | string;
  pool: 'shared' | 'dedicated';
  dialerId: string;
  pilotNumber: string;
}

/** Map raw cliStatus → human label. */
export function cliStatusLabel(s: CliStatus | number | undefined): string {
  if (s === CLI_STATUS.UNARCHIVED) return 'Unarchived';
  if (s === CLI_STATUS.ARCHIVED) return 'Archived';
  return '';
}

/* ============================================================
 *  Allocate / Update / Remove flows
 * ============================================================ */

/** Detail row from GET /api/obd/cli/detail/:cliId. */
export interface CliDetailRow {
  cliId: number;
  cli: string;
  pilotNumber?: string | number;
}

/** Body for POST /api/obd/cli/update — rename a CLI value. */
export interface UpdateCliRequest {
  cliId: number;
  cli: string;
}

/**
 * Body for POST /api/obd/cli — bulk allocate a list of CLIs to a user.
 * `cliList` is a comma-joined string of cliIds. `allocated` is 1 for
 * SADMIN-issued allocations and 2 for ADMIN.
 */
export interface AllocateCliRequest {
  userId: number;
  /** Comma-joined cliId list. */
  cliList: string;
  /** Parent / issuing user id. */
  parent: number;
  allocated: 1 | 2;
}

/**
 * Body for POST /api/obd/api/cli/allocate and /dedicated/cli/allocate —
 * mirrors `ApiCliAllocateRequest` in obd3-api/src/models. The same shape
 * is used for both allocate endpoints; the backend discriminates on
 * `allocationType`:
 *   - manual: `cli` = CSV of cliIds to grant
 *   - bulk:   `cli` = starting cliId, `cliRange` = count
 */
export interface AllocateApiOrDedicatedCliRequest {
  /** Stringified userId — backend reads as string. */
  userId: string;
  allocationType: 'manual' | 'bulk';
  /** Manual: CSV cliIds. Bulk: starting cliId. */
  cli: string;
  /** Range size for bulk allocation, sent as a string. */
  cliRange?: string;
}

/** Body for POST /api/obd/api/remove/cli and /dedicated/remove/cli. */
export interface RemoveApiOrDedicatedCliRequest {
  userId: string;
  /** Comma-joined cliIds — backend reads as string, not array. */
  cliIds: string;
}

/** Lightweight user record returned by GET /api/obd/users/:userId — picker source. */
export interface UserPickerRow {
  userId: number;
  username: string;
  name?: string;
}

/**
 * Body for POST /api/obd/total/cli/usage — cross-user usage summary.
 * Both bounds inclusive, formatted as legacy `yyyy-MM-dd HH:mm:ss`.
 */
export interface TotalCliUsageRequest {
  startDate: string;
  endDate: string;
  userId?: number | string;
}

export interface TotalCliUsageRow {
  cliId: number;
  cli: string;
  totalCalls?: number;
  totalDuration?: number;
  username?: string;
}
